import React, { useEffect, useState } from 'react';
import { Search, Plus, ChevronRight, ChevronDown } from 'lucide-react';
import { useNotes } from '@/context/NotesContext';
import { useFragments } from '@/context/FragmentsContext';
import { useTodosOptional } from '@/context/TodoContext';
import FragmentsSection from '@/components/fragments/FragmentsSection';
import NoteCard from './NoteCard';

/**
 * The right-hand sidebar panel. It is the Notes panel (NotesContext owns
 * `isNotesPanelOpen` and the single collapsed expand tab) split into two
 * collapsible sections — Notes and Fragments — sharing one search box.
 */
const NotesPanel: React.FC = () => {
  const {
    isNotesPanelOpen,
    setIsNotesPanelOpen,
    filteredNotes,
    searchTerm,
    setSearchTerm,
    setCurrentEditingNote,
    setIsEditorModalOpen,
    toggleNoteInjection,
    isLoading,
    error,
    hasLoadedNotes,
    refetchNotes,
  } = useNotes();

  const {
    setSearchTerm: setFragmentsSearchTerm,
    refetchFragments,
  } = useFragments();

  const todosContext = useTodosOptional();

  // Section collapse state is local and intentionally not persisted; both
  // sections start expanded and reset to expanded whenever the panel closes.
  const [isNotesSectionExpanded, setIsNotesSectionExpanded] = useState(true);
  const [isFragmentsSectionExpanded, setIsFragmentsSectionExpanded] =
    useState(true);

  // Closing the panel restores the default (both expanded) so the next open
  // isn't stuck with a section a previous focus request collapsed.
  useEffect(() => {
    if (isNotesPanelOpen) {
      return;
    }
    setIsNotesSectionExpanded(true);
    setIsFragmentsSectionExpanded(true);
  }, [isNotesPanelOpen]);

  const handleNewNote = () => {
    setCurrentEditingNote(null);
    setIsEditorModalOpen(true);
  };

  const handleEditNote = (note: any) => {
    setCurrentEditingNote(note);
    setIsEditorModalOpen(true);
  };

  // One search box drives both sections: notes search round-trips to the
  // backend (debounced in NotesContext), fragments filter client-side.
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setFragmentsSearchTerm(value);
  };

  const handleTogglePanel = async () => {
    const willBeOpen = !isNotesPanelOpen;
    setIsNotesPanelOpen(willBeOpen);

    // Close todos panel when opening notes (mutual exclusion)
    if (willBeOpen && todosContext) {
      todosContext.setIsTodoPanelOpen(false);
    }

    // Both sections live in this panel, so fill both when it is opened
    if (willBeOpen) {
      await Promise.all([refetchNotes(), refetchFragments()]);
    }
  };

  return (
    <>
      {/* Notes Panel */}
      <aside
        className={`bg-card dark:bg-card-dark text-card-foreground dark:text-card-foreground-dark border-l border-border flex flex-col transition-all duration-300 ease-in-out relative ${isNotesPanelOpen ? 'w-96' : 'w-1 overflow-hidden'}`}
      >
        {/* Expand Tab - Shows when collapsed */}
        {!isNotesPanelOpen && (
          <button
            onClick={handleTogglePanel}
            className='absolute left-0 top-1/2 -translate-y-1/2 bg-primary px-1 py-3 rounded-r-lg text-xs font-medium hover:bg-primary/90 transition-all duration-200 z-10 shadow-lg'
            style={{ writingMode: 'vertical-lr' }}
          >
            Notes
          </button>
        )}

        {/* Panel Content - Only shown when open */}
        {isNotesPanelOpen && (
          <>
            {/* Header */}
            <div className='flex items-center justify-between p-4 border-b border-border'>
              <div className='flex items-center gap-2'>
                <button
                  onClick={handleTogglePanel}
                  className='p-1.5 hover:bg-muted dark:hover:bg-selected-item-dark rounded-md transition-colors'
                  title='Collapse Notes'
                >
                  <ChevronRight className='w-4 h-4' />
                </button>
                <h3 className='text-lg font-medium'>Notes &amp; Functions</h3>
              </div>
            </div>

            {/* Content */}
            <div className='flex-1 flex flex-col overflow-hidden'>
              {/* Search - filters both sections */}
              <div className='p-4 border-b border-border'>
                <div className='relative'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4' />
                  <input
                    type='text'
                    placeholder='Search notes and functions...'
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className='w-full pl-10 pr-4 py-2 border border-input rounded-full text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all'
                  />
                </div>
              </div>

              {/* Sections */}
              <div className='flex-1 overflow-y-auto'>
                {/* Notes Section */}
                <section data-testid='notes-section'>
                  <div className='sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-card dark:bg-card-dark border-b border-border'>
                    <button
                      onClick={() =>
                        setIsNotesSectionExpanded((expanded) => !expanded)
                      }
                      aria-expanded={isNotesSectionExpanded}
                      className='flex items-center gap-2 p-1 -ml-1 rounded-md hover:bg-muted dark:hover:bg-selected-item-dark transition-colors'
                      title={
                        isNotesSectionExpanded
                          ? 'Collapse Notes'
                          : 'Expand Notes'
                      }
                    >
                      {isNotesSectionExpanded ? (
                        <ChevronDown className='w-4 h-4' />
                      ) : (
                        <ChevronRight className='w-4 h-4' />
                      )}
                      <h4 className='text-sm font-medium'>Notes</h4>
                      <span
                        className='px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-muted border border-border'
                        data-testid='notes-count'
                      >
                        {filteredNotes.length}
                      </span>
                    </button>

                    <button
                      onClick={handleNewNote}
                      className='flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm'
                      aria-label='New note'
                    >
                      <Plus className='w-4 h-4' />
                      New
                    </button>
                  </div>

                  {isNotesSectionExpanded && (
                    <div className='p-4 space-y-0'>
                      {/* Error State */}
                      {error && (
                        <div className='text-center py-8'>
                          <div className='text-destructive text-sm mb-2'>
                            Error loading notes
                          </div>
                          <div className='text-xs'>{error}</div>
                        </div>
                      )}

                      {/* Loading State */}
                      {isLoading && !error && (
                        <div className='text-center py-8'>
                          <div className='text-sm'>Loading notes...</div>
                        </div>
                      )}

                      {/* Notes Content */}
                      {!isLoading && !error && !hasLoadedNotes ? (
                        <div className='text-center py-8'>
                          <div className='text-sm mb-2'>
                            Click the + button above to load and create notes
                          </div>
                        </div>
                      ) : (
                        !isLoading &&
                        !error && (
                          <>
                            {filteredNotes.length > 0 ? (
                              filteredNotes.map((note) => (
                                <NoteCard
                                  key={note.id}
                                  note={note}
                                  onEdit={handleEditNote}
                                  onToggleInjection={toggleNoteInjection}
                                />
                              ))
                            ) : searchTerm ? (
                              <div className='text-center py-8'>
                                <div className='text-sm'>
                                  No notes found matching "{searchTerm}"
                                </div>
                              </div>
                            ) : (
                              <div className='text-center py-8'>
                                <div className='text-sm mb-2'>No notes yet</div>
                                <button
                                  onClick={handleNewNote}
                                  className='text-sm hover:underline'
                                >
                                  Create your first note
                                </button>
                              </div>
                            )}
                          </>
                        )
                      )}
                    </div>
                  )}
                </section>

                {/* Fragments Section */}
                <FragmentsSection
                  isExpanded={isFragmentsSectionExpanded}
                  onToggleExpanded={() =>
                    setIsFragmentsSectionExpanded((expanded) => !expanded)
                  }
                />
              </div>
            </div>
          </>
        )}

        {/* Collapsed State Background */}
        {!isNotesPanelOpen && (
          <div
            className='absolute inset-0 bg-gradient-to-b from-primary/60 to-primary/40 opacity-60'
            style={{ width: '3px' }}
          />
        )}
      </aside>
    </>
  );
};

export default NotesPanel;
